<template>
  <div class="despesas-categorias-page">
    <div class="page-container">
      <div class="page-header">
        <h3 class="page-title">{{ t('Categorias das despesas') }}</h3>
        <div class="page-actions">
          <Button
            :label="t('Nova Categoria')"
            icon="pi pi-plus"
            data-testid="despesas-categorias-btn-nova"
            @click="handleCreate"
          />
        </div>
      </div>
      <div class="page-body">
        <DespesasCategoriasTable
          @edit="handleEdit"
          @delete="handleDelete"
        />
      </div>
    </div>

    <!-- DespesasCategoriasModal is centralized in ModalManager.vue and opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import DespesasCategoriasTable from './DespesasCategoriasTable.vue';
import { useDespesasCategorias } from '@/composables/useDespesasCategorias';
import { useConfirmAction } from '@/composables/useConfirmAction';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface Categoria {
  despesa_categoria_id: Id;
  despesa_categoria_titulo?: string;
  [key: string]: unknown;
}

const { t } = useI18n();

const { deleteCategoria, fetchCategoria } = useDespesasCategorias();
const { confirmAction } = useConfirmAction();

const handleCreate = (): void => {
  log('DespesasCategoriasPage', 'handleCreate', 'Opening create modal via eventBus');
  eventBus.emit('modal.despesas-categorias.open', { mode: 'create', categoria: null });
};

const handleEdit = async (categoriaId: Id): Promise<void> => {
  log('DespesasCategoriasPage', 'handleEdit', 'Opening edit modal for ID via eventBus', categoriaId);

  try {
    const categoria = await fetchCategoria(categoriaId) as unknown as Categoria | null;

    if (categoria) {
      log('DespesasCategoriasPage', 'handleEdit', 'Found category, opening modal', categoria);
      eventBus.emit('modal.despesas-categorias.open', { mode: 'edit', categoria });
    } else {
      log('DespesasCategoriasPage', 'handleEdit', 'Category not found', { categoriaId });
      eventBus.emit('globalMessage.warn', t('Categoria não encontrada'));
    }
  } catch (error) {
    log('DespesasCategoriasPage', 'handleEdit', 'Error fetching category', error);
    const detail = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar categoria') + ': ' + detail);
  }
};

const handleDelete = (categoria: Categoria): void => {
  log('DespesasCategoriasPage', 'handleDelete', 'Delete requested', categoria);

  confirmAction({
    message: t('Tem certeza que deseja excluir esta categoria?'),
    header: t('Confirmação'),
    onAccept: async () => {
      try {
        await deleteCategoria(categoria.despesa_categoria_id);
        eventBus.emit('globalMessage.success', t('Categoria excluída com sucesso'));
        eventBus.emit('despesas-categorias.refresh');
      } catch (error) {
        log('DespesasCategoriasPage', 'handleDelete', 'Error deleting category', error);
        const detail = error instanceof Error ? error.message : String(error);
        eventBus.emit('globalMessage.error', t('Erro ao excluir categoria') + ': ' + detail);
      }
    }
  });
};

onMounted(() => {
  log('DespesasCategoriasPage', 'onMounted', 'Page mounted');
});
</script>

<style scoped>
.despesas-categorias-page {
  padding: 0;
}

.page-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
}

.page-header {
  padding: 15px 20px;
  border-bottom: 1px solid #f4f4f4;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.page-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.page-actions {
  display: flex;
  gap: 10px;
}

.page-body {
  padding: 20px;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .page-actions {
    width: 100%;
  }

  .page-actions :deep(.p-button) {
    width: 100%;
  }
}
</style>
